import { apiClient } from "@/lib/apiClient";

/** Uzaktan Eğitim Kapısı'ndan senkronlanan eğitim tamamlama kaydı. */
export interface UekEducation {
  id: string;
  educationName: string;
  /** Video / Scorm / ... */
  educationFormat: string | null;
  /** Zorunlu Eğitim / Hizmet İçi Eğitim / ... */
  educationType: string | null;
  registeredAt: string | null;
  completedAt: string | null;
  hasExam: boolean | null;
  /** UEK ham değeri: Başarılı / SinavsizEgitim / ... */
  examStatus: string | null;
}

export interface UekEducationsParams {
  page?: number;
  limit?: number;
  search?: string;
}

interface UekEducationsResponse {
  data?: Partial<UekEducation>[];
  items?: Partial<UekEducation>[];
  count?: number;
  total?: number;
}

export type UekExamChipType = "success" | "error" | "default";

const fetchUekEducations = async (
  url: string,
  params?: UekEducationsParams,
): Promise<{ data: UekEducation[]; total: number }> => {
  const query: Record<string, unknown> = {};
  if (params?.page != null) query.page = params.page;
  if (params?.limit != null) query.limit = params.limit;
  if (params?.search) query.search = params.search;

  const { data } = await apiClient.get<UekEducationsResponse>(url, {
    params: query,
  });
  const items = data?.data ?? data?.items ?? [];
  const rows: UekEducation[] = items.map((item) => ({
    id: item.id ?? "",
    educationName: item.educationName ?? "—",
    educationFormat: item.educationFormat ?? null,
    educationType: item.educationType ?? null,
    registeredAt: item.registeredAt ?? null,
    completedAt: item.completedAt ?? null,
    hasExam: item.hasExam ?? null,
    examStatus: item.examStatus ?? null,
  }));
  return { data: rows, total: data?.count ?? data?.total ?? rows.length };
};

export const uekEducationsApi = {
  /** Kursiyer: GET /api/client/uek-educations/me */
  getMine: (params?: UekEducationsParams) =>
    fetchUekEducations("/api/client/uek-educations/me", params),

  /** Personel detayı: GET /api/admin/uek-educations/users/:userId */
  getByUser: (userId: string, params?: UekEducationsParams) =>
    fetchUekEducations(`/api/admin/uek-educations/users/${userId}`, params),
};

export const formatUekDate = (iso: string | null): string => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("tr-TR");
};

/**
 * UEK sınav durumunu (sinavsayi + sinavdrm) chip etiketine çevirir.
 * `labelKey` doluysa i18n'den, değilse UEK'in ham metni (`label`) gösterilir.
 */
export const getUekExamDisplay = (
  row: Pick<UekEducation, "hasExam" | "examStatus">,
): { labelKey?: string; label?: string; type: UekExamChipType } => {
  const status = row.examStatus?.trim() ?? "";
  if (row.hasExam === false || status === "SinavsizEgitim") {
    return { labelKey: "uekEducations.exam.none", type: "default" };
  }
  if (status === "Başarılı") {
    return { labelKey: "uekEducations.exam.passed", type: "success" };
  }
  if (status === "Başarısız") {
    return { labelKey: "uekEducations.exam.failed", type: "error" };
  }
  return { label: status || "—", type: "default" };
};
